import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { aiCodingJobQueries } from '../../hooks/backend/aiCodingJobs';
import { questionCategoryQueries } from '../../hooks/backend/questionCategories';
import { responseAICodingQueries } from '../../hooks/backend/responseAICodings';
import { fetchQuestionCategorySummary } from '../../services/backend/questionCategories';
import { Question, SurveyWave } from '../../types/domainModels';
import { getJobButtonState } from '../../util/aiCodingJobs';
import { stripHtml } from '../../util/richText';
import ErrorDisplay from '../common/ErrorDisplay';
import Button from '../common/forms/Button';
import Icon from '../common/Icon';
import Modal, { ModalHeader } from '../common/Modal';
import TabGroup, { Tab, TabList } from '../common/Tabs';
import AICodingCategoriesTab from './AICodingCategoriesTab';
import AICodingClassificationTab from './AICodingClassificationTab';
import AICodingSummaryTab from './AICodingSummaryTab';

type AICodingModalTab = 'categories' | 'classification' | 'summary';

interface AICodingConfigModalProps {
  onCloseModal: () => void;
  question: Question;
  waves: SurveyWave[];
}

const AICodingConfigModal = ({
  onCloseModal,
  question,
  waves,
}: AICodingConfigModalProps): JSX.Element => {
  const { id: questionId, isDemographic, sort, title } = question;
  const questionLabel = `${isDemographic ? 'D' : 'Q'}${sort}) ${stripHtml(title)}`;

  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<AICodingModalTab>('categories');
  const [selectedWaveId, setSelectedWaveId] = useState<
    number | null | undefined
  >(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch AI coding jobs status (polls every 5 seconds)
  const { data: aiCodingJobs } = useQuery(
    aiCodingJobQueries.byQuestion({ questionId }),
  );

  const isCategoryGenerationProcessing = getJobButtonState(
    aiCodingJobs,
    'CATEGORY_GENERATION',
    selectedWaveId,
  );
  const isClassificationProcessing = getJobButtonState(
    aiCodingJobs,
    'CLASSIFICATION',
    selectedWaveId,
  );

  // Fetch categories using React Query
  const {
    data: categories = [],
    isLoading,
    error: categoriesError,
  } = useQuery(
    questionCategoryQueries.byQuestion({
      questionId,
      waveId: selectedWaveId ?? undefined,
    }),
  );

  // Fetch classification results
  const {
    data: classificationResults,
    isLoading: isLoadingClassification,
    error: classificationError,
  } = useQuery({
    ...responseAICodingQueries.completedQuestionResponses({
      questionId,
      waveId: selectedWaveId ?? undefined,
    }),
    enabled: activeTab === 'classification',
  });

  // Fetch summary data
  const {
    data: summaryData,
    isLoading: isLoadingSummary,
    error: summaryError,
  } = useQuery({
    queryKey: ['questionCategories', questionId, 'summary', selectedWaveId],
    queryFn: () =>
      fetchQuestionCategorySummary({
        questionId,
        waveId: selectedWaveId ?? undefined,
      }),
    enabled: activeTab === 'summary',
  });

  useEffect(() => {
    if (!isCategoryGenerationProcessing && aiCodingJobs) {
      queryClient.invalidateQueries(
        questionCategoryQueries.byQuestion({
          questionId,
          waveId: selectedWaveId ?? undefined,
        }),
      );
    }
  }, [
    isCategoryGenerationProcessing,
    aiCodingJobs,
    questionId,
    selectedWaveId,
    queryClient,
  ]);

  useEffect(() => {
    if (!isClassificationProcessing && aiCodingJobs) {
      queryClient.invalidateQueries(
        responseAICodingQueries.completedQuestionResponses({
          questionId,
          waveId: selectedWaveId ?? undefined,
        }),
      );
    }
  }, [
    isClassificationProcessing,
    aiCodingJobs,
    questionId,
    selectedWaveId,
    queryClient,
  ]);

  return (
    <Modal
      header={
        <ModalHeader onClickClose={onCloseModal}>{questionLabel}</ModalHeader>
      }
      onCloseModal={onCloseModal}
      size="auto"
    >
      <div className="w-[800px] h-[600px] flex flex-col">
        <div className="mb-6">
          <TabGroup
            onChange={(index) => {
              const tabs: AICodingModalTab[] = [
                'categories',
                'classification',
                'summary',
              ];
              setActiveTab(tabs[index]);
            }}
            selectedIndex={
              activeTab === 'categories'
                ? 0
                : activeTab === 'classification'
                  ? 1
                  : 2
            }
          >
            <TabList size="md">
              <Tab>1. Categories</Tab>
              <Tab>2. Classification</Tab>
              <Tab>3. Summary</Tab>
            </TabList>
          </TabGroup>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorDisplay message={error} />
          </div>
        )}

        <div className="flex-1 overflow-hidden">
          {activeTab === 'categories' && (
            <AICodingCategoriesTab
              categories={categories}
              categoriesError={categoriesError}
              isCategoryGenerationProcessing={isCategoryGenerationProcessing}
              isLoading={isLoading}
              onError={setError}
              onWaveChange={setSelectedWaveId}
              questionId={questionId}
              selectedWaveId={selectedWaveId}
              waves={waves}
            />
          )}

          {activeTab === 'classification' && (
            <AICodingClassificationTab
              categories={categories}
              classificationError={classificationError}
              classificationResults={classificationResults}
              isClassificationProcessing={isClassificationProcessing}
              isLoadingClassification={isLoadingClassification}
              onError={setError}
              questionId={questionId}
              selectedWaveId={selectedWaveId}
            />
          )}

          {activeTab === 'summary' && (
            <AICodingSummaryTab
              isLoadingSummary={isLoadingSummary}
              questionId={questionId}
              summaryData={summaryData}
              summaryError={summaryError}
            />
          )}
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-gray-d-200">
          {activeTab !== 'categories' && (
            <Button
              hierarchy="secondary-gray"
              onClick={() => {
                const tabs: AICodingModalTab[] = [
                  'categories',
                  'classification',
                  'summary',
                ];
                const currentIndex = tabs.indexOf(activeTab);
                if (currentIndex > 0) {
                  setActiveTab(tabs[currentIndex - 1]);
                }
              }}
              size="md"
            >
              Back
            </Button>
          )}

          {activeTab !== 'summary' ? (
            <Button
              hierarchy="primary"
              icon={<Icon id="chevron-right" />}
              iconPlacement="trailing"
              onClick={() => {
                const tabs: AICodingModalTab[] = [
                  'categories',
                  'classification',
                  'summary',
                ];
                const currentIndex = tabs.indexOf(activeTab);
                if (currentIndex < tabs.length - 1) {
                  setActiveTab(tabs[currentIndex + 1]);
                }
              }}
              size="md"
            >
              Next
            </Button>
          ) : (
            <Button
              hierarchy="primary"
              icon={<Icon id="check" />}
              iconPlacement="trailing"
              onClick={onCloseModal}
              size="md"
            >
              Finish
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AICodingConfigModal;
